import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CreditCardOutlined,
  ReloadOutlined,
  SaveOutlined,
  SettingOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import { formatSubscriptionAmount, formatSubscriptionDate } from "../subscriptionFormatters";
import "./SubscriptionOnlineSettings.css";

const { Text, Title } = Typography;

interface GatewayOption {
  store_id: number;
  store_name: string;
  company_name: string;
  api_env: "test" | "prod";
  is_enabled: boolean;
  configured: boolean;
  ready: boolean;
}

interface OnlineSettings {
  gateway_store_id: number | null;
  online_payment_enabled: boolean;
  auto_activate_enabled: boolean;
  annual_discount_months: number;
  youtube_url: string;
  gateway_ready: boolean;
  gateway_environment: "test" | "prod" | null;
  gateway_options: GatewayOption[];
}

interface OnlinePayment {
  id: number;
  reference: string;
  company_name: string;
  plan_name: string;
  billing_period: "monthly" | "annual";
  billable_warehouse_count: number;
  total_amount: number;
  currency: string;
  payment_status: "pending" | "paid" | "failed" | "cancelled";
  activation_status: "pending" | "applied" | "manual_required" | "failed";
  invoice_generation_status: "pending" | "processing" | "completed" | "failed";
  created_at: string;
}

interface SettingsFormValues {
  gateway_store_id: number;
  online_payment_enabled: boolean;
  auto_activate_enabled: boolean;
  annual_discount_months: number;
  youtube_url: string;
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  "Content-Type": "application/json",
});

const paymentStatusLabels: Record<string, string> = {
  pending: "En attente",
  paid: "Payé",
  failed: "Échoué",
  cancelled: "Annulé",
};

const activationStatusLabels: Record<string, string> = {
  pending: "À confirmer",
  applied: "Abonnement activé",
  manual_required: "Validation manuelle",
  failed: "Activation échouée",
};

const SubscriptionOnlineSettings: React.FC = () => {
  const [form] = Form.useForm<SettingsFormValues>();
  const [settings, setSettings] = useState<OnlineSettings | null>(null);
  const [payments, setPayments] = useState<OnlinePayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activatingId, setActivatingId] = useState<number | null>(null);
  const paymentEnabled = Form.useWatch("online_payment_enabled", form);
  const selectedStoreId = Form.useWatch("gateway_store_id", form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsResponse, paymentsResponse] = await Promise.all([
        fetch("/api/subscription-online/admin/settings", { headers: authHeaders() }),
        fetch("/api/subscription-online/admin/payments?limit=30", { headers: authHeaders() }),
      ]);
      const [settingsPayload, paymentsPayload] = await Promise.all([
        settingsResponse.json(),
        paymentsResponse.json(),
      ]);
      if (!settingsResponse.ok || !settingsPayload.success) {
        throw new Error(settingsPayload.message || "Configuration indisponible");
      }
      if (!paymentsResponse.ok || !paymentsPayload.success) {
        throw new Error(paymentsPayload.message || "Paiements indisponibles");
      }
      setSettings(settingsPayload.data);
      setPayments(paymentsPayload.data.payments || []);
      form.setFieldsValue({
        gateway_store_id: settingsPayload.data.gateway_store_id,
        online_payment_enabled: settingsPayload.data.online_payment_enabled,
        auto_activate_enabled: settingsPayload.data.auto_activate_enabled,
        annual_discount_months: settingsPayload.data.annual_discount_months || 1,
        youtube_url: settingsPayload.data.youtube_url,
      });
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Chargement impossible");
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedGateway = useMemo(
    () => settings?.gateway_options.find((option) => option.store_id === selectedStoreId),
    [selectedStoreId, settings],
  );

  const save = useCallback(async (values: SettingsFormValues) => {
    setSaving(true);
    try {
      const response = await fetch("/api/subscription-online/admin/settings", {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          ...values,
          annual_discount_months: 1,
          auto_activate_enabled: Boolean(
            values.online_payment_enabled && values.auto_activate_enabled,
          ),
        }),
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Enregistrement impossible");
      }
      message.success(payload.message);
      await load();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  }, [load]);

  const activate = useCallback(async (payment: OnlinePayment) => {
    setActivatingId(payment.id);
    try {
      const response = await fetch(
        `/api/subscription-online/admin/payments/${payment.id}/activate`,
        { method: "POST", headers: authHeaders() },
      );
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Activation impossible");
      }
      if (payload.data?.invoice_generation_status === "failed") {
        message.warning(payload.message);
      } else {
        message.success(payload.message);
      }
      await load();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Activation impossible");
    } finally {
      setActivatingId(null);
    }
  }, [load]);

  const columns = [
    {
      title: "Entreprise",
      key: "company",
      render: (_: unknown, payment: OnlinePayment) => (
        <div className="subscription-online-settings__identity">
          <strong>{payment.company_name}</strong>
          <span>{payment.reference}</span>
        </div>
      ),
    },
    {
      title: "Offre",
      key: "offer",
      render: (_: unknown, payment: OnlinePayment) => (
        <div className="subscription-online-settings__identity">
          <strong>{payment.plan_name}</strong>
          <span>
            {payment.billing_period === "annual" ? "Annuel · 1 mois offert" : "Mensuel"}
            {` · ${payment.billable_warehouse_count || 1} Warehouse(s)`}
          </span>
        </div>
      ),
    },
    {
      title: "Montant",
      key: "amount",
      render: (_: unknown, payment: OnlinePayment) =>
        formatSubscriptionAmount(payment.total_amount, payment.currency),
    },
    {
      title: "Paiement",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (status: string) => (
        <Tag color={status === "paid" ? "green" : status === "failed" ? "red" : "gold"}>
          {paymentStatusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: "Activation",
      dataIndex: "activation_status",
      key: "activation_status",
      render: (status: string) => (
        <Tag color={status === "applied" ? "green" : status === "failed" ? "red" : "blue"}>
          {activationStatusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: "Facture",
      dataIndex: "invoice_generation_status",
      key: "invoice_generation_status",
      render: (status: string) => (
        <Tag color={status === "completed" ? "green" : status === "failed" ? "red" : "blue"}>
          {status === "completed"
            ? "Générée"
            : status === "failed"
              ? "À régénérer"
              : status === "processing"
                ? "En cours"
                : "En attente"}
        </Tag>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: formatSubscriptionDate,
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, payment: OnlinePayment) => {
        const canActivate =
          payment.payment_status === "paid" && payment.activation_status !== "applied";
        const canRetryInvoice =
          payment.payment_status === "paid"
          && payment.activation_status === "applied"
          && payment.invoice_generation_status === "failed";
        return canActivate || canRetryInvoice ? (
          <Button
            type="primary"
            ghost
            loading={activatingId === payment.id}
            onClick={() => void activate(payment)}
          >
            {canRetryInvoice ? "Régénérer la facture" : "Activer"}
          </Button>
        ) : null;
      },
    },
  ];

  return (
    <Card
      bordered={false}
      loading={loading}
      className="subscription-online-settings"
      title={
        <Space>
          <SettingOutlined />
          <span>Souscription en ligne et YengaPay</span>
        </Space>
      }
    >
      <div className="subscription-online-settings__intro">
        <div>
          <Title level={4}>Parcours de paiement de la landing page</Title>
          <Text type="secondary">
            Réutilisez une configuration YengaPay existante sans copier les clés. Le webhook signé reste la seule source d'activation.
          </Text>
        </div>
        {settings?.gateway_ready && (
          <Tag color="green" icon={<CheckCircleOutlined />}>Passerelle prête</Tag>
        )}
      </div>

      {selectedGateway?.api_env === "test" && paymentEnabled && (
        <Alert
          type="warning"
          showIcon
          message="Passerelle en environnement test"
          description="La landing peut être testée, mais aucun encaissement de production ne doit être annoncé avant le passage de YengaPay en production."
        />
      )}

      <Form form={form} layout="vertical" onFinish={save}>
        <Row gutter={[16, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item
              name="gateway_store_id"
              label="Configuration YengaPay source"
              rules={[{ required: true, message: "Sélectionnez une configuration" }]}
            >
              <Select
                options={(settings?.gateway_options || []).map((option) => ({
                  value: option.store_id,
                  label: `${option.company_name} · ${option.store_name} · ${option.api_env === "prod" ? "Production" : "Test"}${option.ready ? " · Prête" : " · Incomplète"}`,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Form.Item name="online_payment_enabled" label="Paiement sur la landing page" valuePropName="checked">
              <Switch checkedChildren="Activé" unCheckedChildren="Désactivé" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Form.Item name="auto_activate_enabled" label="Activation automatique" valuePropName="checked">
              <Switch disabled={!paymentEnabled} checkedChildren="Activée" unCheckedChildren="Manuelle" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="annual_discount_months" label="Remise annuelle">
              <InputNumber disabled min={1} max={1} addonAfter="mois offert" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={16}>
            <Form.Item
              name="youtube_url"
              label="Chaîne de tutoriels YouTube"
              rules={[{ required: true, type: "url", message: "Lien YouTube valide requis" }]}
            >
              <Input prefix={<YoutubeOutlined />} />
            </Form.Item>
          </Col>
        </Row>
        <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
          Enregistrer les paramètres
        </Button>
      </Form>

      <div className="subscription-online-settings__payments-header">
        <div>
          <Title level={5}><CreditCardOutlined /> Paiements récents</Title>
          <Text type="secondary">Les paiements confirmés restent activables manuellement lorsque l'automatisation est coupée.</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => void load()}>Actualiser</Button>
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={payments}
        pagination={false}
        scroll={{ x: 1050 }}
        locale={{ emptyText: "Aucun paiement de souscription en ligne." }}
      />
    </Card>
  );
};

export default SubscriptionOnlineSettings;
